import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import { Component, computed, DestroyRef, HostBinding, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonComponent } from '@rpgc-ai/ui/button/button.component';
import { SwipeDirective } from '@rpgc-ai/ui/swipe/swipe.directive';

import type { ModalEntry } from '@rpgc-ai/services/modal/modal.service';
import { ModalService } from '@rpgc-ai/services/modal/modal.service';

import type { IModalComponent } from '@rpgc-ai/shared-interfaces/tokens/modal.token';
import { EBackground, MODAL_COMPONENT, Padding, Scroll } from '@rpgc-ai/shared-interfaces/tokens/modal.token';

import { TranslocoPipe } from '@jsverse/transloco';

/**
 * ModalComponent — универсальный компонент модального окна.
 *
 * @link https://yt.devfast.io/youtrack/articles/NT-A-26/Modalnye-okna
 */
@Component({
  selector: 'app-ui-modal',
  templateUrl: './modal.component.html',
  styleUrl: './styles/modal.component.scss',
  imports: [SwipeDirective, NgTemplateOutlet, NgComponentOutlet, ButtonComponent, TranslocoPipe],
  providers: [{ provide: MODAL_COMPONENT, useExisting: ModalComponent }],
})
export class ModalComponent implements OnDestroy, IModalComponent {
  readonly title = model<string>();
  readonly hideCloseButton = model<boolean>(false);
  readonly entry = input<ModalEntry | null>(null);
  readonly #dialogEntry = inject<ModalEntry | null>(DIALOG_DATA, { optional: true });

  readonly dismiss = output<void>();

  @HostBinding('class')
  get applyStylesToHost(): string {
    return this.hostClass();
  }

  readonly isClosing = signal(false);
  readonly paddingStyle = signal<Padding>(Padding.SHOW);
  readonly backgroundStyle = signal<EBackground>(EBackground.SHOW);
  readonly scrollStyle = signal<Scroll>(Scroll.SHOW);
  readonly hostClass = signal<string>('');
  readonly disableCloseOnSwipe = signal(false);
  readonly closeRequestHandler = signal<(() => void) | null>(null);

  readonly #modalService = inject(ModalService);
  readonly #dialogRef = inject(DialogRef, { optional: true });
  readonly #destroyRef = inject(DestroyRef);
  readonly modalEntry = computed<ModalEntry | null>(() => this.entry() ?? this.#dialogEntry ?? null);

  readonly label = computed<string | undefined>(() => this.title() ?? this.modalEntry()?.options?.title);
  // Когда true — снимаем дефолтный tma:pb-safe-0 на .modal_body
  // (для модалок которые сами управляют bottom inset).
  readonly disableTmaBodySafeArea = computed<boolean>(() => this.modalEntry()?.options?.disableTmaBodySafeArea ?? false);

  readonly #animationDuration = 300;
  readonly Padding = Padding;
  readonly EBackground = EBackground;
  readonly Scroll = Scroll;

  #closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.#dialogRef?.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (!this.modalEntry()?.options?.disableOverlayClose) {
          this.requestClose();
        }
      });

    this.#modalService.programmaticClose$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((req) => {
      if (req.entryId !== this.modalEntry()?.id) {
        return;
      }
      if (this.isClosing()) {
        req.done();
        return;
      }
      this.#closeWithOptionalResult(req.result, req.done);
    });
  }

  ngOnDestroy(): void {
    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  handleSwipeDown(): void {
    if (this.disableCloseOnSwipe()) {
      return;
    }
    if (this.closeRequestHandler()) {
      this.requestClose();
      return;
    }
    if (this.modalEntry()?.options?.closeOnlyTopOnSwipe) {
      this.close();
      return;
    }
    this.#closeWithOptionalResult(undefined, () => {
      if (this.#modalService.hasCurrentModal()) {
        this.#modalService.clearStack();
      }
    });
  }

  handleDragHitboxClick(): void {
    if (this.modalEntry()?.options?.disableOverlayClose) {
      return;
    }

    this.requestClose();
  }

  requestClose(): void {
    const handler = this.closeRequestHandler();
    if (handler) {
      handler();
      return;
    }

    this.close();
  }

  close(): void {
    this.#closeWithOptionalResult(undefined);
  }

  closeDialogWithResult(result?: unknown): void {
    this.#closeWithOptionalResult(result);
  }

  #closeWithOptionalResult(result: unknown, onShellExitComplete?: () => void): void {
    this.isClosing.set(true);

    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    this.#closeTimer = setTimeout(() => {
      if (this.#dialogRef) {
        this.#dialogRef.close(result);
      }
      this.isClosing.set(false);
      this.#closeTimer = null;
      onShellExitComplete?.();
    }, this.#animationDuration);

    this.dismiss.emit();
  }
}
